<template>
  <div class="phone-input-wrapper">
    <select
        v-model="selectedCountryCode"
        @change="onCountryChange"
        class="form-input-phone"
    >
      <option value="" disabled>{{ $t('phone_country_select') }}</option>
      <option
          v-for="country in phoneCodes"
          :key="country.iso"
          :value="country.code"
      >
        {{ country.name }} ({{ country.code }})
      </option>
    </select>
    <input
        class="form-input-phone-text"
        :placeholder="$t('enter_phone_number')"
        v-model="phoneNumberInput"
        :disabled="!selectedCountryCode"
        @input="updatePhoneNumber"
        @keydown.backspace="handleBackspace"
    />
  </div>
</template>

<script>
import phoneCodes from '@/assets/phone-codes.json'

export default {
  name: 'PhoneInput',
  props: {
    modelValue: {
      type: String,
      default: '',
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      phoneCodes,
      phoneNumberInput: '',
      selectedCountryCode: '',
      phoneMask: '',
      hasInitialized: false
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (this.hasInitialized) return;

        if (newValue) {
          this.initializeFromValue(newValue);
        }
      }
    }
  },
  computed: {
    computedPhoneNumber() {
      if (this.selectedCountryCode && this.phoneNumberInput) {
        const cleanNumber = this.phoneNumberInput.replace(/\D/g, '');
        return `${this.selectedCountryCode}${cleanNumber}`;
      }
      return '';
    }
  },
  methods: {
    initializeFromValue(phoneNumber) {
      if (!phoneNumber) return;

      const cleanNumber = phoneNumber.replace(/[\s()-]/g, '');

      let bestMatch = null;
      let maxLength = 0;

      for (const country of this.phoneCodes) {
        if (cleanNumber.startsWith(country.code) && country.code.length > maxLength) {
          bestMatch = country;
          maxLength = country.code.length;
        }
      }

      if (bestMatch) {
        this.selectedCountryCode = bestMatch.code;
        this.phoneMask = bestMatch.mask || '###-###-####';
        const numberWithoutCode = cleanNumber.substring(bestMatch.code.length);
        this.phoneNumberInput = this.applyMask(this.phoneMask, numberWithoutCode);
        this.hasInitialized = true;
      }
    },
    onCountryChange() {
      const country = this.phoneCodes.find(c => c.code === this.selectedCountryCode);
      if (country) {
        this.phoneMask = country.mask || '###-###-####';
      }
      this.phoneNumberInput = '';
      this.hasInitialized = true;
      this.emitValue();
    },
    handleBackspace(event) {
      if (event.key === 'Backspace' && this.phoneNumberInput) {
        const cursorPosition = event.target.selectionStart;
        const value = this.phoneNumberInput;

        if (cursorPosition > 0 && /[-\s]/.test(value[cursorPosition - 1])) {
          event.preventDefault();
          const beforeSpecialChar = value.substring(0, cursorPosition - 2);
          const afterSpecialChar = value.substring(cursorPosition);
          this.phoneNumberInput = beforeSpecialChar + afterSpecialChar;

          this.$nextTick(() => {
            event.target.setSelectionRange(cursorPosition - 2, cursorPosition - 2);
          });
        }
      }
    },
    updatePhoneNumber() {
      const rawNumber = this.phoneNumberInput.replace(/\D/g, '');

      if (this.phoneMask) {
        this.phoneNumberInput = this.applyMask(this.phoneMask, rawNumber);
      }

      this.emitValue();
    },
    applyMask(mask, input) {
      const cleanMask = mask.replace(/\d/g, '#');

      let masked = '';
      let inputIndex = 0;

      for (let i = 0; i < cleanMask.length && inputIndex < input.length; i++) {
        if (cleanMask[i] === '#') {
          masked += input[inputIndex];
          inputIndex++;
        } else {
          if (!/\d/.test(mask[i])) {
            masked += cleanMask[i];
          }
        }
      }

      return masked;
    },
    emitValue() {
      this.$emit('update:modelValue', this.computedPhoneNumber);
    }
  }
}
</script>

<style scoped>
.phone-input-wrapper {
  display: flex;
  align-items: center;
  border-radius: 5px;
  gap: 8px;
}

.form-input-phone {
  width: 40%;
  outline: none;
}

.form-input-phone-text {
  outline: none;
  flex: 1;
}

.form-input-phone-text:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.form-input-phone option {
  font-family: inherit;
}
</style>
